import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';
import { dynastyTheme, withAlpha } from '../../../../../services/colorService';

const C = dynastyTheme.tokens.colors;

/**
 * CardTabNavigator - Horizontal scrollable tab navigator for MobilePlayerCard
 * 
 * Features:
 * - 5 main tabs (Overview, Career, Game Logs, Analytics, More)
 * - Horizontal scroll for extra tabs
 * - Active state indicator (yellow underline)
 * - Sticky positioning below hero section
 * - Smooth scroll on tab selection
 * - Height: 44px
 * 
 * Used in: MobilePlayerCard
 */

const CardTabNavigator = ({ tabs, activeTab, onChange, isCondensed = false }) => {
  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const dropdownRef = useRef(null);
  const tabRefs = useRef([]);

  // Sub-tabs for More dropdown.
  // 9l.1: 'News' added at first position — pre-existing NewsTabContent component
  // (used in MobilePlayerModal) had never been wired into the full-profile card.
  // Tony's screenshot: clicking More showed dropdown but no News option — fixed here.
  const subTabs = ['News', 'History', 'Advanced', 'Contract'];

  const handleTabClick = (tab, index) => {
    setFocusedIndex(index);
    if (tab === 'More') {
      // Toggle dropdown
      setIsMoreDropdownOpen(!isMoreDropdownOpen);
    } else {
      // Close dropdown and change tab
      setIsMoreDropdownOpen(false);
      if (onChange) {
        onChange(tab);
      }
    }
  };

  // Keyboard navigation for tabs
  const handleKeyDown = (e, tab, index) => {
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        const nextIndex = (index + 1) % tabs.length;
        setFocusedIndex(nextIndex);
        tabRefs.current[nextIndex]?.focus();
        break;
      
      case 'ArrowLeft':
        e.preventDefault();
        const prevIndex = (index - 1 + tabs.length) % tabs.length;
        setFocusedIndex(prevIndex);
        tabRefs.current[prevIndex]?.focus();
        break;
      
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleTabClick(tab, index);
        break;
      
      case 'Escape':
        if (isMoreDropdownOpen) {
          e.preventDefault();
          setIsMoreDropdownOpen(false);
        }
        break;
      
      default:
        break;
    }
  };

  const handleSubTabClick = (subTab) => {
    setIsMoreDropdownOpen(false);
    if (onChange) {
      onChange(subTab);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsMoreDropdownOpen(false);
      }
    };

    if (isMoreDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMoreDropdownOpen]);

  // Check if active tab is a sub-tab
  const isSubTabActive = subTabs.includes(activeTab);
  const isMoreActive = activeTab === 'More' || isSubTabActive;

  // Calculate top position based on condensed state
  // Top bar (56px) + Hero (60px condensed, 120px full)
  const topPosition = isCondensed ? '116px' : '176px';
  
  return (
    <div style={{ ...styles.container, top: topPosition }} ref={dropdownRef} role="tablist" aria-label="Player profile tabs">
      <div style={styles.tabsWrapper}>
        {tabs.map((tab, index) => {
          const isActive = tab === 'More' ? isMoreActive : activeTab === tab;
          const isMoreTab = tab === 'More';
          
          return (
            <button
              key={tab}
              ref={(el) => (tabRefs.current[index] = el)}
              onClick={() => handleTabClick(tab, index)}
              onKeyDown={(e) => handleKeyDown(e, tab, index)}
              style={{
                ...styles.tab,
                ...(isActive ? styles.tabActive : {}),
                minWidth: '44px',
                minHeight: '44px',
              }}
              role="tab"
              tabIndex={isActive ? 0 : -1}
              aria-label={`${tab} tab${isActive ? ' (selected)' : ''}`}
              aria-selected={isActive}
              aria-expanded={isMoreTab ? isMoreDropdownOpen : undefined}
              aria-haspopup={isMoreTab ? 'menu' : undefined}
            >
              <span style={{
                ...( isActive ? styles.tabTextActive : styles.tabText),
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}>
                {tab}
                {isMoreTab && (
                  <ChevronDown 
                    size={14} 
                    style={{
                      transform: isMoreDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                    }}
                  />
                )}
              </span>
              {isActive && <div style={styles.activeIndicator} />}
            </button>
          );
        })}
      </div>

      {/* More Dropdown */}
      {isMoreDropdownOpen && (
        <div 
          style={styles.dropdown}
          role="menu"
          aria-label="More options"
        >
          {subTabs.map((subTab) => {
            const isSubActive = activeTab === subTab;
            return (
              <button
                key={subTab}
                onClick={() => handleSubTabClick(subTab)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSubTabClick(subTab);
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    setIsMoreDropdownOpen(false);
                  }
                }}
                style={{
                  ...styles.dropdownItem,
                  ...(isSubActive ? styles.dropdownItemActive : {}),
                  minHeight: '44px',
                }}
                role="menuitem"
                aria-label={`${subTab} tab${isSubActive ? ' (selected)' : ''}`}
              >
                <span style={isSubActive ? styles.dropdownTextActive : styles.dropdownText}>
                  {subTab}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

CardTabNavigator.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeTab: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  isCondensed: PropTypes.bool,
};

// ============================================================================
// STYLES
// ============================================================================

const styles = {
  container: {
    position: 'sticky',
    // top is set dynamically based on isCondensed prop
    left: 0,
    right: 0,
    height: '44px',
    background: C.surface[800],
    borderBottom: `1px solid ${C.surface[900]}`,
    zIndex: 80,
    overflow: 'hidden',
  },

  tabsWrapper: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none', // Firefox
    msOverflowStyle: 'none', // IE/Edge
    paddingLeft: '4px',
    paddingRight: '4px',
  },

  tab: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: '0 16px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'background 0.1s ease',
  },

  tabActive: {
    background: withAlpha(C.gold.bright, 0.05),
  },

  tabText: {
    fontSize: '14px',
    fontWeight: '500',
    color: C.coolGray[400],
    whiteSpace: 'nowrap',
    transition: 'color 0.2s ease',
  },

  tabTextActive: {
    fontSize: '14px',
    fontWeight: '600',
    color: C.white,
    whiteSpace: 'nowrap',
  },

  activeIndicator: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: C.gold.bright,
    borderRadius: '2px 2px 0 0',
  },

  dropdown: {
    position: 'absolute',
    top: '44px',
    right: '4px',
    background: C.surface[800],
    border: `1px solid ${C.surface[900]}`,
    borderRadius: '8px',
    boxShadow: `0 4px 12px ${withAlpha(C.black, 0.3)}`,
    zIndex: 90,
    minWidth: '140px',
    overflow: 'hidden',
    animation: 'slideDown 0.2s ease',
  },

  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '44px',
    padding: '0 16px',
    background: 'transparent',
    border: 'none',
    borderBottom: `1px solid ${C.surface[900]}`,
    cursor: 'pointer',
    transition: 'background 0.1s ease',
  },

  dropdownItemActive: {
    background: withAlpha(C.gold.bright, 0.1),
  },

  dropdownText: {
    fontSize: '14px',
    fontWeight: '500',
    color: C.coolGray[400],
    whiteSpace: 'nowrap',
  },

  dropdownTextActive: {
    fontSize: '14px',
    fontWeight: '600',
    color: C.gold.bright,
    whiteSpace: 'nowrap',
  },
};

// Hide scrollbar
if (typeof window !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    /* Hide scrollbar for Chrome, Safari and Opera */
    .tabsWrapper::-webkit-scrollbar {
      display: none;
    }
  `;
  document.head.appendChild(styleSheet);
}

export default CardTabNavigator;
